import { ReactNode } from 'react';

import { PREVISIT_INSIGHTS_ADVANCE_MINUTES } from '@eluve/api-contract';
import {
  Button,
  Heading,
  Icon,
  SimpleTooltip,
  Stack,
  Text,
} from '@eluve/components';
import { usePrevisitInsights } from '@eluve/frontend-appointment-hooks';
import type { AppointmentStatusEnum } from '@eluve/graphql-types';
import { useTranslation } from '@eluve/i18n';
import { InsightsPanel } from '@eluve/insights';

interface WebPrevisitInsightsProps {
  appointmentId: string;
  /**
   * The patient the appointment is currently assigned to. Threaded into
   * usePrevisitInsights so it can invalidate insights generated for a
   * previously-assigned patient (ENG-4601). When absent, an empty-state prompt
   * is shown and the generating hook is never mounted (see below).
   */
  patientId?: string;
  /** Appointment start (ISO); suppresses auto-generation while beyond the advance window. */
  startDate?: string | null;
  /** When set, blocks generation for ineligible statuses; an existing artifact still shows. */
  status?: AppointmentStatusEnum | null;
}

/**
 * Title row for the Insights tab: title + AI-disclaimer tooltip, with the
 * regenerate button as `actions` (omitted in the no-patient empty state).
 */
const InsightsHeader = ({ actions }: { actions?: ReactNode }) => {
  const { t } = useTranslation();
  return (
    <Stack align="center" justify="between" gap={2} direction="horizontal">
      <Stack align="center" gap={2} direction="horizontal">
        <Heading color="secondary" size="lg" weight="semibold">
          {t('appointmentPage.previsitInsights.title')}
        </Heading>
        <SimpleTooltip label={t('appointmentPage.previsitInsights.tooltip')}>
          <div className="mt-0.5 cursor-help">
            <Icon name="CircleHelp" size="sm" className="text-text-tertiary" />
          </div>
        </SimpleTooltip>
      </Stack>
      {actions}
    </Stack>
  );
};

/**
 * Owns the insights hook for the inline panel. Mounted only once a patient is
 * assigned, so usePrevisitInsights triggers generation on first view rather
 * than persisting a no-data artifact for a patient-less appointment.
 *
 * Renders InsightsHeader instead of InsightsPanel's built-in title +
 * headerActions because the header must also render in the no-patient empty
 * state, where this component (and thus InsightsPanel) is deliberately
 * unmounted — sharing one local header across both branches beats two
 * implementations that can drift.
 */
const ConnectedPanel = ({
  appointmentId,
  patientId,
  startDate,
  status,
}: WebPrevisitInsightsProps) => {
  const { t } = useTranslation();
  const {
    spec,
    status: insightsStatus,
    isAwaitingWindow,
    isGenerationBlocked,
    isLoading,
    error,
    loadingStartedAt,
    regenerate,
  } = usePrevisitInsights({ appointmentId, patientId, startDate, status });

  // InsightsPanel renders nothing without a spec, so blocked-with-no-artifact
  // needs its own empty state.
  if (isGenerationBlocked && !spec) {
    return (
      <>
        <InsightsHeader />
        <Stack gap={3} align="center" className="py-8 text-center">
          <Icon name="CalendarX2Icon" className="text-text-tertiary" />
          <Text size="sm" color="secondary">
            {t('appointment.insights.blockedTitle')}
          </Text>
          <Text size="sm" color="tertiary">
            {t('appointment.insights.blockedMessage')}
          </Text>
        </Stack>
      </>
    );
  }

  if (isAwaitingWindow) {
    return (
      <>
        <InsightsHeader />
        <Stack gap={3} align="center" className="py-8 text-center">
          <Icon name="Clock" className="text-text-tertiary" />
          <Text size="sm" color="tertiary">
            {t('appointmentPage.previsitInsights.autoGenerateNotice', {
              minutes: PREVISIT_INSIGHTS_ADVANCE_MINUTES,
            })}
          </Text>
          <Button variant="outline" size="sm" onClick={() => void regenerate()}>
            {t('appointmentPage.previsitInsights.generateNow')}
          </Button>
        </Stack>
      </>
    );
  }

  const regenerateAction = isGenerationBlocked ? (
    <SimpleTooltip label={t('appointment.insights.generateBlockedTooltip')}>
      <Button
        variant="outline"
        size="sm"
        aria-label={t('appointment.insights.generate')}
        disabled
      >
        <Icon name="RefreshCw" size="auto" />
      </Button>
    </SimpleTooltip>
  ) : (
    <SimpleTooltip label={t('appointment.insights.generate')}>
      <Button
        variant="outline"
        size="sm"
        aria-label={t('appointment.insights.generate')}
        onClick={isLoading ? undefined : () => void regenerate()}
        disabled={isLoading}
        aria-hidden={isLoading || undefined}
        tabIndex={isLoading ? -1 : undefined}
        // Hidden (not unmounted) while loading so the header doesn't reflow.
        className={isLoading ? 'invisible' : undefined}
      >
        <Icon name="RefreshCw" size="auto" />
      </Button>
    </SimpleTooltip>
  );

  return (
    <>
      <InsightsHeader actions={regenerateAction} />
      <InsightsPanel
        spec={spec}
        status={insightsStatus}
        isLoading={isLoading}
        loadingStartedAt={loadingStartedAt}
        error={error}
        appointmentId={appointmentId}
        hideTitle
      />
    </>
  );
};

/**
 * Web previsit-insights surface: the body of the appointment sidebar's
 * "Insights" tab. Sources insights from the patient's local signed notes (no
 * EHR required) via the generatePrevisitInsights workflow's no-EHR branch. The
 * WEB_PREVISIT_INSIGHTS rollout flag is read by the parent, which decides
 * whether the Insights tab exists at all.
 *
 * Until a patient is assigned, an empty-state prompt is shown and the
 * generating hook (ConnectedPanel) is never mounted, so no no-data artifact is
 * persisted (ENG-4601). There is no launcher button and no dismiss control —
 * surfacing insights is a deliberate workflow step, not an optional drawer
 * (Caroline Mao's design).
 */
export const WebPrevisitInsights = ({
  appointmentId,
  patientId,
  startDate,
  status,
}: WebPrevisitInsightsProps) => {
  const { t } = useTranslation();
  return (
    <Stack gap={3}>
      {patientId ? (
        <ConnectedPanel
          appointmentId={appointmentId}
          patientId={patientId}
          startDate={startDate}
          status={status}
        />
      ) : (
        <>
          <InsightsHeader />
          <Stack gap={2} align="center" className="py-8 text-center">
            <Icon name="UserRoundSearch" className="text-text-tertiary" />
            <Text size="sm" color="tertiary">
              {t('appointmentPage.previsitInsights.selectPatient')}
            </Text>
          </Stack>
        </>
      )}
    </Stack>
  );
};
